import React, { useEffect, useMemo, useRef, useState } from 'react';
import { procurementService, LeituraDeSenha } from '../../services/api';
import CascaDeModal from '../shared/CascaDeModal';
import PreencherTitulares from './PreencherTitulares';
import { A_PROPRIA_LISTA_DIZ_O_VAZIO, EstadoDaLista, campo, falhaIgnoradaDeProposito } from '../../design';
import { CELULA, CascaDaLista, TabelaDaLista, CabecalhoDaLista, ColunaDaLista, LinhaDaLista, CelulaAcoes, barraDoEstado } from '../shared/lista/PecasDaLista';
import { fmtDateTime } from '../../utils/formatters';
import { TONE_BADGE, TONE_TEXT } from '../../utils/statusColors';

/**
 * ACESSOS AOS PORTAIS, REDESENHO (decisões do Heitor, 23/10/2026: A1, S1, O1 — a parte de front; titular, data da troca de
 * senha e o registro de quem copiou/mostrou vêm do backend, com o Bastião).
 *
 * A tela serve para ACHAR o login de um portal rápido e COPIAR usuário e senha com segurança:
 *  - busca no topo (atalho "/") por portal ou login; os acessos do mesmo portal agrupados ("Banrisul" com os 4 titulares);
 *  - saem Sistema e Empresa, que eram o mesmo valor nos 46 acessos;
 *  - login e senha MASCARADOS; "copiar" põe na área de transferência sem mostrar; "Mostrar" fica no ⋯ e some em 10 s;
 *  - a chamada da senha diz o USO (copiar | mostrar), para o backend registrar quem fez o quê.
 * Parte 2 (backend #532): o TITULAR em coluna própria (preenchido pela prévia conferida, "Preencher titulares"); a data da
 * TROCA DE SENHA (sem registro = null, nunca deduzida do updatedAt), com a barra âmbar e o filtro "Senha antiga" depois de
 * 365 dias (D1); o selo "usado pelo robô" (já houve disputa com o acesso); e o histórico de quem copiou ou mostrou.
 */
export interface AcessoAoPortal {
  id: number;
  portalName: string;
  portalUrl?: string | null;
  login?: string | null;
  hasPassword?: boolean;
  isActive?: boolean;
  titular?: string | null;
  /** ISO; null = sem registro (os acessos antigos). Gravada só pelo backend, quando a senha muda. */
  senhaTrocadaEm?: string | null;
  /** Já houve sessão de disputa com este acesso (não "em disputa agora"). */
  usadoPeloRobo?: boolean;
  [k: string]: any;
}

/** D1: depois deste prazo, a senha é "antiga" (barra âmbar e filtro). */
export const DIAS_PARA_SENHA_ANTIGA = 365;

/** Dias desde a troca da senha, ou null quando não há registro. */
export function diasDesdeATroca(iso: string | null | undefined, agora: Date = new Date()): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((agora.getTime() - t) / 86400000));
}
export const senhaAntiga = (p: AcessoAoPortal, agora: Date = new Date()) => {
  const d = diasDesdeATroca(p.senhaTrocadaEm, agora);
  return d !== null && d > DIAS_PARA_SENHA_ANTIGA;
};
const fraseDaTroca = (d: number | null) => (d === null ? 'sem registro' : d === 0 ? 'hoje' : d === 1 ? 'há 1 dia' : `há ${d} dias`);

/** A família do portal: o nome sem o complemento entre parênteses ("Banrisul (Fabio)" → "Banrisul"). */
export function familiaDoPortal(nome: string): string {
  return String(nome || '').replace(/\s*\([^)]*\)\s*$/, '').trim() || String(nome || '').trim();
}

/** Os acessos que casam com a busca (portal ou login, sem acento e sem caixa). */
export function filtrarAcessos(lista: AcessoAoPortal[], termo: string): AcessoAoPortal[] {
  const norm = (s: any) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const t = norm(termo).trim();
  if (!t) return lista;
  return lista.filter((p) => norm(p.portalName).includes(t) || norm(p.login).includes(t) || norm(p.titular).includes(t));
}

/** Agrupa pela família, na ordem alfabética da família e, dentro dela, do nome. */
export function agruparPorFamilia(lista: AcessoAoPortal[]): { familia: string; acessos: AcessoAoPortal[] }[] {
  const mapa = new Map<string, AcessoAoPortal[]>();
  for (const p of lista) {
    const f = familiaDoPortal(p.portalName);
    mapa.set(f, [...(mapa.get(f) || []), p]);
  }
  const cmp = (a: string, b: string) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base', numeric: true });
  return Array.from(mapa.entries())
    .sort(([a], [b]) => cmp(a, b))
    .map(([familia, acessos]) => ({ familia, acessos: [...acessos].sort((x, y) => cmp(x.portalName, y.portalName)) }));
}

const MASCARA = '••••••••';
type FiltroDeEstado = 'todos' | 'ativos' | 'inativos' | 'antiga';

async function copiarTexto(texto: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(texto); return true; }
  } catch (e) {
    falhaIgnoradaDeProposito('a API de área de transferência falhou: o caminho de baixo (execCommand) tenta de novo, e o aviso diz se não deu')(e);
  }
  try {
    const t = document.createElement('textarea');
    t.value = texto; t.style.position = 'fixed'; t.style.opacity = '0';
    document.body.appendChild(t); t.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(t);
    return ok;
  } catch { return false; }
}

export default function AcessosAosPortais({ portais, carga, onTentarDeNovo, onNovo, onEditar, onExcluir, avisar, Icon, onTitularesAplicados }: {
  portais: AcessoAoPortal[];
  carga: any;
  onTentarDeNovo: () => void;
  onNovo?: () => void;
  onEditar: (p: AcessoAoPortal) => void;
  onExcluir: (p: AcessoAoPortal) => void;
  avisar: (mensagem: string, tipo?: 'success' | 'error') => void;
  Icon: React.FC<{ name: string; className?: string }>;
  /** Depois de gravar titulares: recarregar a lista. */
  onTitularesAplicados?: () => void;
}) {
  const [preenchendo, setPreenchendo] = useState(false);
  const [historico, setHistorico] = useState<{ acesso: AcessoAoPortal; leituras: LeituraDeSenha[] | null; erro: string | null } | null>(null);
  const abrirHistorico = async (p: AcessoAoPortal) => {
    setHistorico({ acesso: p, leituras: null, erro: null });
    try {
      const leituras = await procurementService.getPortalPasswordLog(p.id, 50);
      setHistorico({ acesso: p, leituras: Array.isArray(leituras) ? leituras : [], erro: null });
    } catch (e: any) {
      setHistorico({ acesso: p, leituras: null, erro: e?.response?.data?.message || e?.message || 'o servidor não respondeu' });
    }
  };
  const [busca, setBusca] = useState('');
  const [estado, setEstado] = useState<FiltroDeEstado>('todos');
  const [revelado, setRevelado] = useState<Record<number, { login?: string | null; senha?: string }>>({});
  const campoDaBusca = useRef<HTMLInputElement>(null);

  // "/" leva à busca, como nos sites de busca; não rouba a tecla de quem já está digitando.
  useEffect(() => {
    const ao = (e: KeyboardEvent) => {
      const alvo = e.target as HTMLElement | null;
      const digitando = alvo && (alvo.tagName === 'INPUT' || alvo.tagName === 'TEXTAREA' || alvo.isContentEditable);
      if (e.key === '/' && !digitando) { e.preventDefault(); campoDaBusca.current?.focus(); }
    };
    window.addEventListener('keydown', ao);
    return () => window.removeEventListener('keydown', ao);
  }, []);

  const contagem = useMemo(() => ({
    todos: portais.length,
    ativos: portais.filter((p) => p.isActive !== false).length,
    inativos: portais.filter((p) => p.isActive === false).length,
    antiga: portais.filter((p) => senhaAntiga(p)).length,
  }), [portais]);

  const grupos = useMemo(() => {
    const porEstado = portais.filter((p) => (estado === 'todos' ? true : estado === 'ativos' ? p.isActive !== false : estado === 'inativos' ? p.isActive === false : senhaAntiga(p)));
    return agruparPorFamilia(filtrarAcessos(porEstado, busca));
  }, [portais, estado, busca]);
  const achados = grupos.reduce((n, g) => n + g.acessos.length, 0);

  const copiarLogin = async (p: AcessoAoPortal) => {
    if (!p.login) return;
    const ok = await copiarTexto(p.login);
    avisar(ok ? `Login de ${p.portalName} copiado` : 'Não consegui copiar o login', ok ? 'success' : 'error');
  };
  const copiarSenha = async (p: AcessoAoPortal) => {
    try {
      const { password } = await procurementService.getPortalPassword(p.id, 'copiar');
      const ok = await copiarTexto(password);
      avisar(ok ? `Senha de ${p.portalName} copiada` : 'Não consegui copiar a senha', ok ? 'success' : 'error');
    } catch { avisar('Não consegui ler a senha', 'error'); }
  };
  const mostrar = async (p: AcessoAoPortal) => {
    try {
      const { password } = p.hasPassword ? await procurementService.getPortalPassword(p.id, 'mostrar') : { password: undefined };
      setRevelado((r) => ({ ...r, [p.id]: { login: p.login, senha: password } }));
      setTimeout(() => setRevelado((r) => { const n = { ...r }; delete n[p.id]; return n; }), 10000);
    } catch { avisar('Não consegui ler a senha', 'error'); }
  };

  const Copiavel = ({ texto, aberto, onCopiar, rotulo }: { texto?: string | null; aberto?: string | null; onCopiar: () => void; rotulo: string }) => (
    texto === null || texto === undefined || texto === '' ? <span className='text-slate-600'>—</span> : (
      <button type='button' onClick={onCopiar} title={`Copiar ${rotulo} (não mostra na tela)`} data-copiar={rotulo}
        className='inline-flex items-center gap-2 px-2 py-1 rounded-md border border-central-borda text-xs hover:bg-central-elevado'>
        <span className='font-mono text-central-texto'>{aberto ?? MASCARA}</span>
        <span className='text-slate-400'>copiar</span>
      </button>
    )
  );

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap items-center gap-2'>
        <div className='relative flex-1 min-w-[240px]'>
          <Icon name='search' className='w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2' />
          <input
            ref={campoDaBusca}
            type='text'
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder='Buscar portal, titular ou login…'
            aria-label='Buscar acesso'
            className={`w-full rounded-lg pl-9 pr-10 py-2 text-sm placeholder-slate-500 ${campo}`}
          />
          <kbd className='absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 border border-central-borda rounded px-1.5'>/</kbd>
        </div>
        <div className='inline-flex rounded-lg border border-central-borda overflow-hidden' role='group' aria-label='Estado'>
          {([['todos', 'Todos'], ['ativos', 'Ativos'], ['inativos', 'Inativos'], ['antiga', 'Senha antiga']] as const).map(([k, r]) => (
            <button key={k} type='button' aria-pressed={estado === k} onClick={() => setEstado(k)}
              className={`px-3 py-2 text-sm border-l border-central-borda first:border-l-0 ${estado === k ? 'bg-central-elevado text-white font-semibold' : 'bg-central-cartao text-slate-400 hover:bg-central-elevado'}`}>
              {r} ({contagem[k]})
            </button>
          ))}
        </div>
        <button type='button' onClick={() => setPreenchendo(true)} className='px-3 py-2 border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto rounded-lg text-sm'>
          Preencher titulares
        </button>
        {onNovo && (
          <button onClick={onNovo} className='flex items-center gap-2 px-3 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg text-sm'>
            <Icon name='plus' className='w-4 h-4' /> Novo Acesso
          </button>
        )}
      </div>

      <EstadoDaLista carga={carga} temDados={carga.atualizadoEm !== null} oQue='os acessos aos portais' onTentarDeNovo={onTentarDeNovo} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
        <CascaDaLista>
          <TabelaDaLista>
            <CabecalhoDaLista>
              <ColunaDaLista rotulo='Portal' />
              <ColunaDaLista rotulo='Titular' largura='w-44' />
              <ColunaDaLista rotulo='Login' largura='w-48' />
              <ColunaDaLista rotulo='Senha' largura='w-48' />
              <ColunaDaLista rotulo='Senha trocada' largura='w-36' />
              <ColunaDaLista rotulo='Estado' largura='w-36' alinhamento='centro' />
              <ColunaDaLista rotulo='Ações' largura='w-24' alinhamento='direita' />
            </CabecalhoDaLista>
            <tbody className='divide-y divide-central-borda'>
              {achados === 0 ? (
                <tr><td colSpan={7} className='px-4 py-10 text-center text-slate-500' data-acessos='vazio'>
                  {portais.length === 0 ? 'Nenhum acesso cadastrado' : `Nenhum acesso encontrado${busca ? ` para "${busca}"` : ''}`}
                </td></tr>
              ) : grupos.map((g) => (
                <React.Fragment key={g.familia}>
                  {g.acessos.length > 1 && (
                    <tr className='bg-central-elevado/60' data-grupo={g.familia}>
                      <td colSpan={7} className='px-4 py-1.5 text-[11px] uppercase tracking-wider text-slate-400'>
                        {g.familia} · {g.acessos.length} acessos
                      </td>
                    </tr>
                  )}
                  {g.acessos.map((p) => {
                    const aberto = revelado[p.id];
                    const dias = diasDesdeATroca(p.senhaTrocadaEm);
                    const antiga = senhaAntiga(p);
                    return (
                      <LinhaDaLista key={p.id} apagada={p.isActive === false} data-acesso={p.id} data-senha-antiga={antiga ? '' : undefined}>
                        <td className={`${CELULA} ${barraDoEstado(antiga ? 'warning' : null)}`}>
                          <div className='flex items-center gap-2 min-w-0'>
                            <span className='text-sm font-medium text-central-texto truncate'>{p.portalName}</span>
                            {p.portalUrl && (
                              <a href={p.portalUrl} target='_blank' rel='noopener noreferrer' title={`Abrir ${p.portalName}`}
                                className='text-slate-400 hover:text-white text-xs whitespace-nowrap hover:underline'>abrir ↗</a>
                            )}
                          </div>
                        </td>
                        <td className={`${CELULA} text-sm truncate`} data-titular>
                          {p.titular ? <span className='text-central-texto'>{p.titular}</span> : <span className='text-slate-600'>—</span>}
                        </td>
                        <td className={CELULA}>
                          <Copiavel texto={p.login} aberto={aberto?.login} rotulo='login' onCopiar={() => copiarLogin(p)} />
                        </td>
                        <td className={CELULA}>
                          {p.hasPassword
                            ? <Copiavel texto='•' aberto={aberto?.senha} rotulo='senha' onCopiar={() => copiarSenha(p)} />
                            : <span className='text-slate-600'>—</span>}
                        </td>
                        <td className={`${CELULA} text-sm whitespace-nowrap ${antiga ? TONE_TEXT.warning : dias === null ? 'text-slate-500' : 'text-slate-300'}`} data-troca
                          title={p.senhaTrocadaEm ? `Senha trocada em ${fmtDateTime(p.senhaTrocadaEm)}` : 'Sem registro da troca: os acessos anteriores a 23/10/2026 não têm a data'}>
                          {fraseDaTroca(dias)}
                        </td>
                        <td className={`${CELULA} text-center`}>
                          <div className='flex flex-wrap justify-center gap-1'>
                            {p.isActive === false && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TONE_BADGE.neutral}`}>Inativo</span>}
                            {p.usadoPeloRobo && (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TONE_BADGE.neutral}`} data-robo
                                title='Já houve sessão de disputa com este acesso: ao trocar a senha, atualize o robô.'>usado pelo robô</span>
                            )}
                          </div>
                        </td>
                        <CelulaAcoes
                          rotuloDoMenu={`Mais ações do acesso ${p.portalName}`}
                          onEditar={() => onEditar(p)}
                          Icon={Icon}
                          itens={[
                            { label: 'Mostrar login e senha (10 s)', onClick: () => mostrar(p) },
                            { label: 'Quem copiou ou mostrou a senha', onClick: () => abrirHistorico(p) },
                            { label: 'Excluir', onClick: () => onExcluir(p), danger: true, separadoAntes: true },
                          ]}
                        />
                      </LinhaDaLista>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </TabelaDaLista>
        </CascaDaLista>
      </EstadoDaLista>
      {preenchendo && <PreencherTitulares onFechar={() => setPreenchendo(false)} onAplicado={() => onTitularesAplicados?.()} />}
      {historico && (
        <CascaDeModal largura='sm' titulo={`Senha de ${historico.acesso.portalName}: quem copiou ou mostrou`} onClose={() => setHistorico(null)} corpoClassName='p-4'>
          {historico.erro ? (
            <p role='alert' className='text-sm text-red-300'>Não consegui ler o histórico: {historico.erro}</p>
          ) : historico.leituras === null ? (
            <p className='text-sm text-slate-400'>Carregando o histórico…</p>
          ) : historico.leituras.length === 0 ? (
            <p className='text-sm text-slate-400' data-historico='vazio'>Ninguém copiou nem mostrou esta senha desde que o registro começou.</p>
          ) : (
            <ul className='divide-y divide-central-borda text-sm' data-historico>
              {historico.leituras.map((l) => (
                <li key={l.id} className='py-2 flex items-center justify-between gap-3'>
                  <span className='text-central-texto'>{l.usuario}</span>
                  <span className='text-slate-400'>{l.uso === 'copiar' ? 'copiou' : 'mostrou'} · {fmtDateTime(l.em)}</span>
                </li>
              ))}
            </ul>
          )}
        </CascaDeModal>
      )}
    </div>
  );
}
